
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fetchUsers, syncUsers, toggleUserChatAccess } from "@/services/userService";
import { fetchSubscriptions, updateUserSubscription } from "@/services/subscriptionService";
import { useAdminPanelState } from "./useAdminPanelState";
import { useUserSessions } from "./useUserSessions";
import { createRouteAwareChannelName, shouldProcessEvent, isInRoute } from "@/utils/routeManager";

interface UserWithStats {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  status_chat: boolean | null;
  last_access: string | null;
  casos_count: number;
  conversations_count: number;
  documents_count: number;
  tokens_input: number;
  tokens_output: number;
  total_tokens: number;
  limit_tokens: number;
}

export const useAdminPanel = () => {
  const { user } = useAuth();
  const { isAdmin, isLoading: isLoadingAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState<string | null>(null);
  const { 
    filteredUsers, 
    searchQuery, 
    setSearchQuery,
    connectionFilter,
    setConnectionFilter,
    planFilter,
    setPlanFilter,
    chatAccessFilter,
    setChatAccessFilter
  } = useAdminPanelState(users);
  const { getUserSessionInfo, refetch: refetchSessions } = useUserSessions();
  
  // Controles ESPECÍFICOS e ISOLADOS para admin com verificação rigorosa de rota
  const dataLoadInProgress = useRef(false);
  const adminListenerSetup = useRef(false);
  const adminChannelRef = useRef<any>(null);
  const stableAdminState = useRef(false);
  const adminRouteActive = useRef(false);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isInAdminRoute = currentPath.startsWith('/paineladmin');
    
    if (!isInAdminRoute) {
      console.log('Admin: Hook carregado fora da rota admin - redirecionando');
      navigate("/");
      return;
    }
    
    // Aguardar verificação de admin antes de decidir
    if (isLoadingAdmin) {
      return;
    }
    
    if (user && !isAdmin) {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta página.",
        variant: "destructive",
      });
      navigate("/");
    } else if (!user) {
      navigate("/");
    } else if (isAdmin && isInAdminRoute) {
      fetchData();
    }
  }, [user, isAdmin, isLoadingAdmin, navigate, toast, window.location.pathname]);

  // Listener otimizado - APENAS para mudanças locais no admin com verificação rigorosa de rota
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isInAdminRoute = currentPath.startsWith('/paineladmin');
    
    if (user && isAdmin && !adminListenerSetup.current &&
        stableAdminState.current && isInAdminRoute) {
      console.log('Admin: Configurando listener local ÚNICO e ISOLADO para rota admin');
      adminListenerSetup.current = true;
      adminRouteActive.current = true;
      
      // Listener isolado APENAS para atualizações locais no admin
      const channelName = createRouteAwareChannelName('admin-local-updates');
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { 
            event: 'UPDATE', 
            schema: 'public', 
            table: 'users'
          },
          (payload) => {
            // Verificação TRIPLA: contexto admin, rota correta, autorização
            if (shouldProcessEvent('/paineladmin') && isInRoute('/paineladmin') && 
                isAdmin) {
              console.log('Admin: Update local recebido (apenas visual e isolado):', payload);
              
              // Atualizar APENAS localmente no admin sem propagação
              setUsers(currentUsers => 
                currentUsers.map(u => 
                  u.id === payload.new.id ? { ...u, ...payload.new } : u
                )
              );
              
              toast({
                title: "🔄 Dados atualizados",
                description: "Informações do usuário foram sincronizadas",
                duration: 3000,
              });
            } else {
              console.log('Admin: Update ignorado - contexto incorreto');
            }
          }
        )
        .subscribe();
      
      adminChannelRef.current = channel;
      
      return () => {
        console.log('Admin: Removendo listener local ÚNICO do admin');
        if (adminChannelRef.current) {
          supabase.removeChannel(adminChannelRef.current);
          adminChannelRef.current = null;
        }
        adminListenerSetup.current = false;
        adminRouteActive.current = false;
      };
    }
  }, [user?.id, stableAdminState.current, window.location.pathname]);

  // Verificar se a rota mudou para fora do admin
  useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/paineladmin') && adminRouteActive.current) {
        console.log('Admin: Rota mudou para fora do admin - limpando listeners');
        if (adminChannelRef.current) {
          supabase.removeChannel(adminChannelRef.current);
          adminChannelRef.current = null;
          adminListenerSetup.current = false;
          stableAdminState.current = false;
          adminRouteActive.current = false;
        }
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Cleanup geral
  useEffect(() => {
    return () => {
      if (adminChannelRef.current) {
        console.log('Admin: Cleanup final e DEFINITIVO do useAdminPanel');
        supabase.removeChannel(adminChannelRef.current);
        adminChannelRef.current = null;
        adminListenerSetup.current = false;
        stableAdminState.current = false;
        adminRouteActive.current = false;
      }
    };
  }, []);

  const fetchUsersWithStats = async (): Promise<UserWithStats[]> => {
    try {
      console.log('Admin: Buscando usuários via edge function get-user-stats...');
      
      // Usar edge function para buscar estatísticas com service role
      const { data: response, error } = await supabase.functions.invoke('get-user-stats', {
        headers: {
          Authorization: `Bearer ${await supabase.auth.getSession().then(s => s.data.session?.access_token)}`
        }
      });

      if (error) {
        console.error('Admin: Erro na edge function get-user-stats:', error);
        throw error;
      }

      if (!response.success) {
        console.error('Admin: Edge function retornou erro:', response.error);
        throw new Error(response.error);
      }

      const usersWithStats = response.users as UserWithStats[];
      
      console.log(`Admin: Processados ${usersWithStats.length} usuários via edge function`);
      console.log(`Admin: Totais - ${response.totals.conversations} conversas, ${response.totals.documents} documentos`);

      // Log detalhado dos usuários principais para debug
      const debugUsers = ['Marlon', 'Wagner', 'Samuel', 'Enrique'];
      usersWithStats.forEach(user => {
        const userName = user.display_name || user.email;
        if (debugUsers.some(name => userName.includes(name))) {
          console.log(`📊 ${userName}: ${user.conversations_count} conversas, ${user.documents_count} documentos`);
        }
      });

      return usersWithStats;
    } catch (error) {
      console.error("Admin: Erro ao buscar usuários com estatísticas:", error);
      throw error;
    }
  };

  const fetchData = async () => {
    if (!isInRoute('/paineladmin')) {
      console.log('Admin: Carregamento ignorado - rota incorreta');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Admin: Carregando dados do painel com nova estrutura');
      const [usersData, subscriptionsData] = await Promise.all([
        fetchUsersWithStats(),
        fetchSubscriptions()
      ]);
      
      setUsers(usersData);
      setSubscriptions(subscriptionsData);
      stableAdminState.current = true;
      
      // Também refetch das sessões para garantir dados atualizados
      await refetchSessions();
      
      console.log('Admin: Dados carregados com sucesso da nova estrutura');
      console.log('Usuários carregados:', usersData.length);
      console.log('Assinaturas carregadas:', subscriptionsData.length);
    } catch (error) {
      console.error("Admin: Erro ao carregar dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing || !isInRoute('/paineladmin')) return;
    
    setIsRefreshing(true);
    try {
      console.log("Admin: Atualizando dados manualmente");
      await fetchData();
      toast({
        title: "Dados atualizados",
        description: "Os dados do painel foram atualizados com sucesso.",
        variant: "default",
      });
    } catch (error) {
      console.error("Admin: Erro ao atualizar dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar os dados.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSyncUsers = async () => {
    if (isSyncing || !isInRoute('/paineladmin')) return;
    
    setIsSyncing(true);
    try {
      console.log("Admin: Iniciando sincronização manual");
      const syncResult = await syncUsers();
      
      toast({
        title: "Usuários sincronizados",
        description: syncResult?.message || "Os dados dos usuários foram sincronizados com sucesso.",
        variant: "default",
      });
      
      // Aguardar e recarregar dados
      setTimeout(async () => {
        await fetchData();
      }, 1500);
    } catch (error) {
      console.error("Admin: Erro ao sincronizar usuários:", error);
      toast({
        title: "Erro",
        description: "Não foi possível sincronizar os usuários. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUpdateStorageLimit = async (userId: string, planType: 'free' | 'basic' | 'pro' | 'premium') => {
    if (isUpdatingPlan === userId) {
      console.log('Admin: Atualização já em andamento para este usuário');
      return;
    }

    setIsUpdatingPlan(userId);
    
    try {
      console.log('Admin: Atualizando plano do usuário:', userId, 'para', planType);
      
      const newSubscription = await updateUserSubscription(userId, planType);
      console.log('Admin: Plano atualizado com sucesso', newSubscription);
      
      // Aguardar um pouco e recarregar dados
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const [updatedUsersData, updatedSubscriptions] = await Promise.all([
        fetchUsersWithStats(),
        fetchSubscriptions()
      ]);
      
      setUsers(updatedUsersData);
      setSubscriptions(updatedSubscriptions);
      
      const planNames = {
        'free': 'Gratuito',
        'basic': 'Básico', 
        'pro': 'Pro',
        'premium': 'Premium'
      };
      
      toast({
        title: "Plano atualizado",
        description: `O plano do usuário foi atualizado para ${planNames[planType]}.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Admin: Erro ao atualizar limite de armazenamento:", error);
      
      // CORREÇÃO 3: Expandir filtro de erro para incluir mais tipos de logs do Supabase
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      // Filtrar erros que são apenas logs/warnings internos do Supabase
      const isSupabaseWarning = errorMessage.toLowerCase().includes('warning') || 
                               errorMessage.toLowerCase().includes('log') ||
                               errorMessage.toLowerCase().includes('notice') ||
                               errorMessage.toLowerCase().includes('info') ||
                               errorMessage.toLowerCase().includes('debug') ||
                               errorMessage.toLowerCase().includes('hint') ||
                               errorMessage.toLowerCase().includes('detail') ||
                               errorMessage.toLowerCase().includes('context') ||
                               errorMessage.toLowerCase().includes('constraint');
      
      // Só mostrar toast para erros realmente críticos
      if (!isSupabaseWarning) {
        toast({
          title: "Erro",
          description: "Não foi possível atualizar o plano do usuário. Tente novamente.",
          variant: "destructive",
        });
      } else {
        console.log('Admin: Aviso/warning do Supabase ignorado (não é erro crítico):', errorMessage);
      }
    } finally {
      setIsUpdatingPlan(null);
    }
  };

  const handleToggleApproval = async (userId: string, currentStatus: boolean) => {
    try {
      console.log('Admin: Alterando status de chat:', userId, '!', currentStatus);
      const success = await toggleUserChatAccess(userId, currentStatus);
      
      if (success) {
        // Atualizar APENAS localmente no admin sem propagação externa
        setUsers(currentUsers => 
          currentUsers.map(u => 
            u.id === userId ? { ...u, status_chat: !currentStatus } : u
          )
        );
        
        toast({
          title: "Status atualizado",
          description: `Acesso ao chat ${!currentStatus ? 'liberado' : 'bloqueado'} para o usuário.`,
          variant: "default",
        });
      }
      
      return success;
    } catch (error) {
      console.error("Admin: Erro ao atualizar status de acesso ao chat:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status de acesso ao chat do usuário.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    isLoading,
    users,
    filteredUsers,
    subscriptions,
    isRefreshing,
    isSyncing,
    isUpdatingPlan,
    searchQuery,
    setSearchQuery,
    connectionFilter,
    setConnectionFilter,
    planFilter,
    setPlanFilter,
    chatAccessFilter,
    setChatAccessFilter,
    handleRefresh,
    handleSyncUsers,
    handleUpdateStorageLimit,
    handleToggleApproval,
    getUserSessionInfo,
  };
};
